import { useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useProjectsStore } from '../store/projects';
import { useMarketingStore, EditorialWeek, ContentPillar } from '../store/marketing';
import { StatsCard, StatsCardGrid } from '../components/StatsCard';
import { GENERIC_STATS, EDITORIAL_STATUS } from '../config/statusConfig';
import { PageHeader } from '../components/PageHeader';

// MarketingIcon - matches header navigation (bar chart icon)
function MarketingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

// Status labels with accent colors for consistent card styling
const STATUS_STYLES: Record<string, { cardBg: string; accent: string; dot: string }> = {
  planned: { cardBg: 'bg-slate-50', accent: 'border-l-slate-400', dot: 'bg-slate-400' },
  in_progress: { cardBg: 'bg-amber-50', accent: 'border-l-amber-500', dot: 'bg-amber-500' },
  ready: { cardBg: 'bg-blue-50', accent: 'border-l-blue-500', dot: 'bg-blue-500' },
  published: { cardBg: 'bg-green-50', accent: 'border-l-green-500', dot: 'bg-green-500' },
  skipped: { cardBg: 'bg-zinc-50', accent: 'border-l-zinc-400', dot: 'bg-zinc-400' },
};

// Funnel styles
const FUNNEL_STYLES: Record<string, { color: string; bg: string }> = {
  awareness: { color: 'text-blue-700', bg: 'bg-blue-100' },
  consideration: { color: 'text-purple-700', bg: 'bg-purple-100' },
  activation: { color: 'text-green-700', bg: 'bg-green-100' },
};

// Format icons
const FORMAT_ICONS: Record<string, string> = {
  carousel: '📸',
  reel: '🎬',
  story: '📱',
  single_graphic: '🖼️',
  text: '📝',
};

export function MarketingPage() {
  const { t } = useTranslation(['marketing', 'common']);
  const { isAuthenticated } = useAuthStore();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const {
    pillars,
    editorialWeeks,
    stats,
    currentYear,
    isLoading,
    fetchPillars,
    fetchEditorialPlan,
    fetchStats,
    setCurrentYear,
    setSelectedQuarter,
  } = useMarketingStore();
  const navigate = useNavigate();

  // Auth check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProjects();
  }, [isAuthenticated, navigate, fetchProjects]);

  // Auto-select first project if none selected
  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId, setSelectedProjectId]);

  // Reset quarter filter when loading main marketing page (load all weeks)
  useEffect(() => {
    setSelectedQuarter(null);
  }, [setSelectedQuarter]);

  // Load marketing data when project changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchPillars(selectedProjectId);
      // Load all weeks (no quarter filter) for the overview
      fetchEditorialPlan(selectedProjectId, currentYear, undefined);
      fetchStats(selectedProjectId, currentYear);
    }
  }, [selectedProjectId, currentYear, fetchPillars, fetchEditorialPlan, fetchStats]);

  // Get current week number
  const getCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now.getTime() - start.getTime();
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    return Math.ceil(diff / oneWeek);
  };

  // Group weeks by quarter
  const weeksByQuarter = useMemo(() => {
    const grouped: Record<number, EditorialWeek[]> = { 1: [], 2: [], 3: [], 4: [] };
    editorialWeeks.forEach((week) => {
      if (grouped[week.quarter]) {
        grouped[week.quarter].push(week);
      }
    });
    return grouped;
  }, [editorialWeeks]);

  // Get upcoming weeks (next 4)
  const upcomingWeeks = useMemo(() => {
    const currentWeek = getCurrentWeek();
    return editorialWeeks
      .filter((w) => w.week_number >= currentWeek)
      .slice(0, 4);
  }, [editorialWeeks]);

  // Get pillar by ID
  const getPillar = (pillarId: string | null): ContentPillar | undefined => {
    return pillars.find((p) => p.id === pillarId);
  };

  // Get status label and styles
  const getStatusInfo = (status: string) => {
    const statusKey = status === 'in_progress' ? 'inProgress' : status;
    return {
      label: t(`marketing:status.${statusKey}`),
      color: status === 'planned' ? 'text-slate-700' :
             status === 'in_progress' ? 'text-amber-700' :
             status === 'ready' ? 'text-blue-700' :
             status === 'published' ? 'text-green-700' : 'text-zinc-500',
      bg: status === 'planned' ? 'bg-slate-100' :
          status === 'in_progress' ? 'bg-amber-100' :
          status === 'ready' ? 'bg-blue-100' :
          status === 'published' ? 'bg-green-100' : 'bg-zinc-100',
      ...STATUS_STYLES[status],
    };
  };

  // Render week card
  const WeekCard = ({ week }: { week: EditorialWeek }) => {
    const pillar = getPillar(week.pillar_id);
    const funnel = FUNNEL_STYLES[week.funnel_stage];
    const status = getStatusInfo(week.status);
    const currentWeek = getCurrentWeek();
    const isCurrent = week.week_number === currentWeek && week.year === currentYear;
    const isPast = week.week_number < currentWeek && week.year === currentYear;

    return (
      <div
        onClick={() => navigate(`/marketing/week/${week.year}/${week.week_number}?project_id=${selectedProjectId}`)}
        className={`rounded-lg border p-4 hover:shadow-md transition-all cursor-pointer border-l-[5px] ${status.accent} ${status.cardBg} ${
          isCurrent ? 'ring-2 ring-blue-500' : 'border-gray-200'
        } ${isPast && week.status !== 'published' ? 'opacity-60' : ''}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500">{t('marketing:calendar.week')} {week.week_number}</span>
            {isCurrent && (
              <span className="px-1.5 py-0.5 text-[10px] bg-blue-500 text-white rounded">
                {t('marketing:dashboard.now')}
              </span>
            )}
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${status.bg} ${status.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        </div>

        {/* Pillar indicator */}
        {pillar && (
          <div className="flex items-center gap-1.5 mb-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: pillar.color }}
            />
            <span className="text-xs text-gray-500">{pillar.name}</span>
          </div>
        )}

        {/* Topic */}
        <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
          {week.main_topic}
        </h4>
        {week.main_topic_subtitle && (
          <p className="text-xs text-gray-500 line-clamp-1 mb-2">
            {week.main_topic_subtitle}
          </p>
        )}

        {/* Format & Funnel */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm" title={week.format}>
            {FORMAT_ICONS[week.format] || '📄'}
          </span>
          <span className={`px-1.5 py-0.5 text-[10px] rounded ${funnel?.bg} ${funnel?.color}`}>
            {t(`marketing:funnel.${week.funnel_stage}`)}
          </span>
          {week.master_asset_type && (
            <span className="text-[10px] text-gray-400">{week.master_asset_type}</span>
          )}
        </div>
      </div>
    );
  };

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  if (!selectedProject) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('marketing:dashboard.noProjectAvailable')}</h3>
          <p className="text-gray-500 mb-4">{t('marketing:dashboard.createProjectFirst')}</p>
          <Link
            to="/projects"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('marketing:dashboard.createProject')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        icon={<MarketingIcon className="w-7 h-7" />}
        title={t('marketing:title')}
        subtitle={t('marketing:subtitle')}
        actions={
          <div className="flex items-center gap-3">
            {/* Project selector */}
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            {/* Year selector with stepper */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setCurrentYear(Math.max(2020, currentYear - 1))}
                className="px-2 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
                title={t('marketing:dashboard.previousYear')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <input
                type="number"
                min={2020}
                max={2099}
                value={currentYear}
                onChange={(e) => {
                  const year = parseInt(e.target.value);
                  if (year >= 2020 && year <= 2099) {
                    setCurrentYear(year);
                  }
                }}
                className="w-16 px-1 py-2 text-center text-sm font-medium border-0 focus:ring-0 focus:outline-none"
              />
              <button
                onClick={() => setCurrentYear(Math.min(2099, currentYear + 1))}
                className="px-2 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
                title={t('marketing:dashboard.nextYear')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">{t('common:loading')}</div>
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          {stats && (
            <div className="mb-8">
              <StatsCardGrid columns={5}>
                <StatsCard
                  value={stats.totalWeeks}
                  label={t('marketing:dashboard.weeksPlanned')}
                  config={GENERIC_STATS.weeks}
                />
                <StatsCard
                  value={stats.plannedWeeks}
                  label={t('marketing:status.planned')}
                  config={EDITORIAL_STATUS.planned}
                />
                <StatsCard
                  value={stats.inProgressWeeks + stats.readyWeeks}
                  label={t('marketing:dashboard.inProgressReady')}
                  config={EDITORIAL_STATUS.in_progress}
                />
                <StatsCard
                  value={stats.publishedWeeks}
                  label={t('marketing:status.published')}
                  config={EDITORIAL_STATUS.published}
                />
                <StatsCard
                  value={pillars.length}
                  label={t('marketing:dashboard.contentPillars')}
                  config={GENERIC_STATS.pillars}
                />
              </StatsCardGrid>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Calendar Overview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Upcoming Weeks */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{t('marketing:dashboard.nextWeeks')}</h2>
                  <Link
                    to={`/marketing/calendar?project_id=${selectedProjectId}`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('marketing:dashboard.openCalendar')}
                  </Link>
                </div>

                {upcomingWeeks.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-500 text-sm">{t('marketing:dashboard.noWeeksPlanned')}</p>
                    <Link
                      to={`/marketing/calendar?project_id=${selectedProjectId}`}
                      className="inline-block mt-3 text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t('marketing:dashboard.createEditorialPlan')}
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {upcomingWeeks.map((week) => (
                      <WeekCard key={week.id} week={week} />
                    ))}
                  </div>
                )}
              </div>

              {/* Quarter Overview */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('marketing:dashboard.quarterOverview')} {currentYear}</h2>

                <div className="space-y-4">
                  {[1, 2, 3, 4].map((quarter) => {
                    const weeks = weeksByQuarter[quarter] || [];
                    const published = weeks.filter((w) => w.status === 'published').length;
                    const total = weeks.length;
                    const progress = total > 0 ? (published / total) * 100 : 0;

                    // Funnel distribution
                    const awareness = weeks.filter((w) => w.funnel_stage === 'awareness').length;
                    const consideration = weeks.filter((w) => w.funnel_stage === 'consideration').length;
                    const activation = weeks.filter((w) => w.funnel_stage === 'activation').length;

                    return (
                      <div
                        key={quarter}
                        onClick={() => {
                          setSelectedQuarter(quarter);
                          navigate(`/marketing/calendar?project_id=${selectedProjectId}&quarter=${quarter}`);
                        }}
                        className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">Q{quarter}</span>
                          <span className="text-sm text-gray-500">
                            {published}/{total} {t('marketing:dashboard.weeks')}
                          </span>
                        </div>

                        {/* Progress bar */}
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>

                        {/* Funnel distribution */}
                        {total > 0 && (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-blue-600">
                              {awareness} {t('marketing:funnel.awareness')}
                            </span>
                            <span className="text-purple-600">
                              {consideration} {t('marketing:funnel.consideration')}
                            </span>
                            <span className="text-green-600">
                              {activation} {t('marketing:funnel.activation')}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Pillars & Quick Actions */}
            <div className="space-y-6">
              {/* Content Pillars */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{t('marketing:dashboard.contentPillars')}</h2>
                  <Link
                    to={`/marketing/pillars?project_id=${selectedProjectId}`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('marketing:dashboard.manage')}
                  </Link>
                </div>

                {pillars.length === 0 ? (
                  <div className="text-center py-6">
                    <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    <p className="text-sm text-gray-500 mb-2">{t('marketing:dashboard.noPillarsDefined')}</p>
                    <Link
                      to={`/marketing/pillars?project_id=${selectedProjectId}`}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t('marketing:dashboard.createPillars')}
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pillars.map((pillar) => {
                      const pillarWeeks = editorialWeeks.filter((w) => w.pillar_id === pillar.id);
                      return (
                        <div
                          key={pillar.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/marketing/pillars/${pillar.id}?project_id=${selectedProjectId}`)}
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: pillar.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {pillar.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {pillarWeeks.length} {t('marketing:pillars.weeks')}
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">
                            {pillar.recommended_formats?.join(', ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Funnel Balance */}
              {stats && stats.byFunnelStage.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('marketing:dashboard.funnelBalance')}</h2>

                  <div className="space-y-3">
                    {['awareness', 'consideration', 'activation'].map((stage) => {
                      const data = stats.byFunnelStage.find((s) => s.stage === stage);
                      const count = data?.count || 0;
                      const total = stats.totalWeeks || 1;
                      const percentage = Math.round((count / total) * 100);
                      const funnel = FUNNEL_STYLES[stage];

                      // Target percentages
                      const targets: Record<string, number> = {
                        awareness: 40,
                        consideration: 40,
                        activation: 20,
                      };
                      const target = targets[stage];
                      const isOnTarget = Math.abs(percentage - target) <= 10;

                      return (
                        <div key={stage}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm ${funnel.color}`}>{t(`marketing:funnel.${stage}`)}</span>
                            <span className={`text-sm ${isOnTarget ? 'text-green-600' : 'text-amber-600'}`}>
                              {percentage}% ({count}) / {t('marketing:dashboard.target')}: {target}%
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                stage === 'awareness'
                                  ? 'bg-blue-500'
                                  : stage === 'consideration'
                                  ? 'bg-purple-500'
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-gray-400 mt-4">
                    {t('marketing:dashboard.funnelTargetHint')}
                  </p>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('marketing:dashboard.quickAccess')}</h2>
                <div className="grid grid-cols-1 gap-3">
                  <Link
                    to={`/marketing/calendar?project_id=${selectedProjectId}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('marketing:dashboard.weekCalendar52')}</span>
                  </Link>
                  <Link
                    to={`/marketing/pillars?project_id=${selectedProjectId}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    <span className="text-sm font-medium">{t('marketing:dashboard.managePillars')}</span>
                  </Link>
                  <Link
                    to={`/marketing/templates?project_id=${selectedProjectId}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                    <span className="text-sm font-medium">{t('marketing:dashboard.templateLibrary')}</span>
                  </Link>
                  <Link
                    to="/marketing/analytics"
                    className="flex items-center gap-3 p-3 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('marketing:dashboard.instagramAnalytics')}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
